import { useState } from 'react'
import type { CqRange } from '../api/connectionQuality'
import { Content } from '../ui/Shell'
import { queryToZoom } from './cqZoom'
import { CQ_DEFAULT_STATE, CQ_LAYOUTS, CQ_RANGES, CqView, type CqLayout, type CqViewState } from './DeviceConnectionQuality'

/**
 * Connection Quality pe o pagina a lui, mare - butonul "Open in new tab" de pe
 * fila fisei. Tiparul fisei CMDB (`/cmdb/view/{id}`, deschisa in tab nou din
 * liste): o adresa proprie, in aplicatie, cu schelet cu tot.
 *
 *   /app/inventoree/device/{id}/connection-quality?range=4h&from=2026-09-30T10:00&to=2026-09-30T12:00&view=full
 *
 * Perioada si zoom-ul stau in adresa, deci ce vezi se poate copia si trimite,
 * iar inapoi/inainte in browser merge pe ele. Drepturile sunt ale serverului:
 * acelasi cap ca fila (`opteemum.live_devices_network_status.read`, aparatul
 * firmei alese) - fara drept ori pe alt aparat, pagina scrie ce a raspuns el.
 *
 * Doua vederi (30 septembrie 2026): Grid - carduri de rezumat si grafice mici,
 * unul langa altul (CqGrid.tsx), implicita - si Full, tot mare, una sub alta.
 * Alegerea sta in adresa (`view`) si in browser (localStorage), ca pagina
 * deschisa data viitoare sa vina cum a lasat-o omul; adresa bate browserul.
 */

export type CqFullSearch = { range?: string; from?: string; to?: string; view?: string }

export const CQ_LAYOUT_KEY = 'od.cq.fullPage.view'

function isLayout(v: unknown): v is CqLayout {
  return CQ_LAYOUTS.includes(v as CqLayout)
}

function storedLayout(): CqLayout | null {
  try {
    const v = window.localStorage.getItem(CQ_LAYOUT_KEY)
    return isLayout(v) ? v : null
  } catch {
    return null
  }
}

function storeLayout(layout: CqLayout): void {
  try {
    window.localStorage.setItem(CQ_LAYOUT_KEY, layout)
  } catch {
    /* Browser fara localStorage (privat, blocat): ramane doar in adresa. */
  }
}

/** Vederea: din adresa, altfel ce a lasat omul in browser, altfel Grid. */
export function layoutFromSearch(search: CqFullSearch): CqLayout {
  if (isLayout(search.view)) return search.view
  return storedLayout() ?? 'grid'
}

/** Starea din adresa; ce nu se intelege cade pe implicit (24h, fara zoom). */
export function stateFromSearch(search: CqFullSearch): CqViewState {
  const range = CQ_RANGES.includes(search.range as CqRange) ? (search.range as CqRange) : CQ_DEFAULT_STATE.range
  return { range, zoom: queryToZoom(search.from, search.to) }
}

export function searchFromState(state: CqViewState): CqFullSearch {
  return {
    range: state.range === CQ_DEFAULT_STATE.range ? undefined : state.range,
    from: state.zoom?.from,
    to: state.zoom?.to,
  }
}

export function ConnectionQualityFullPage({
  id,
  search,
  onSearch,
}: {
  id: number
  search: CqFullSearch
  onSearch: (search: CqFullSearch) => void
}) {
  /* Citita o data, la intrare; apoi o tine adresa. */
  const [fallback] = useState<CqLayout>(() => layoutFromSearch({}))
  const layout = isLayout(search.view) ? search.view : fallback
  if (!Number.isFinite(id) || id < 1) {
    return (
      <Content>
        <div className="alert alert-danger">
          <i className="fas fa-info-circle"></i> Device is undefined
        </div>
      </Content>
    )
  }
  const state = stateFromSearch(search)
  return (
    <Content>
      <div className="cq-full">
        <CqView
          id={id}
          big
          state={state}
          onState={(s) => onSearch({ ...searchFromState(s), view: search.view })}
          layout={layout}
          onLayout={(l) => {
            storeLayout(l)
            onSearch({ ...searchFromState(state), view: l })
          }}
        />
      </div>
    </Content>
  )
}
